import {
  Box,
  Button,
  type ButtonProps,
  useRadio,
  useRadioGroup,
  Wrap,
  type WrapProps,
} from '@chakra-ui/react'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import React, { useState } from 'react'

import FormContentBlock from '@/components/UI/Form/FormContentBlock'
import { FormContext, type FormDirection, type FormInputType } from '@/components/UI/Form/types'
import Icon from '@/components/UI/Icon/Icon'

type RadioInput = FormInputType & { type: 'radio' }

const AUTO_SUBMIT_DELAY_MS = 400

function InputRadioButton({
  label,
  direction,
  icon,
  radioPayload,
  buttonProps = {},
  iconColor = 'primary.fg',
  formOnSubmit,
}: {
  label: RadioInput['options'][number]['label']
  direction: FormDirection
  icon?: IconDefinition
  radioPayload: Record<string, unknown>
  iconColor?: string
  buttonProps?: ButtonProps
  formOnSubmit?: () => void
}): JSX.Element {
  const { getInputProps, getRadioProps } = useRadio(radioPayload)

  const { onChange: inputOnChange, ...inputProps } = getInputProps()
  const onChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    inputOnChange?.(e)
    if (formOnSubmit != null) {
      setTimeout(() => {
        formOnSubmit()
      }, AUTO_SUBMIT_DELAY_MS)
    }
  }
  const radioProps = getRadioProps()

  return (
    <Button
      {...radioProps}
      variant="outline"
      as="label"
      leftIcon={icon != null ? <Icon faIcon={icon} size="md" color={iconColor} /> : undefined}
      borderRadius="8px"
      justifyContent="center"
      textAlign="left"
      _checked={{
        bg: 'primary.subtle',
        borderColor: 'primary.border',
      }}
      _focus={{ boxShadow: 'outline' }}
      flex={direction === 'horizontal' ? 1 : 0}
      flexBasis={0}
      whiteSpace="normal"
      height="auto"
      {...buttonProps}
    >
      <input {...inputProps} onChange={onChange} />
      <Box>
        <FormContentBlock content={label} />
      </Box>
    </Button>
  )
}

const InputRadioButtons: React.FC<
  {
    input: FormInputType
    buttonProps?: ButtonProps
    triggerSubmitOnOptionSelected?: boolean
    iconColor?: string
    valueToIcon?: (v: string) => IconDefinition
  } & WrapProps
> = ({
  input,
  buttonProps,
  triggerSubmitOnOptionSelected = false,
  iconColor = 'primary.fg',
  valueToIcon,
  ...props
}) => {
  if (input.type !== 'radio') {
    throw new Error('Invariant violation')
  }

  // Because standard radios don't support "unchecking" options, we need
  // to handle checking/unchecking ourselves.
  const [checkedValue, setCheckedValue] = useState<string | null>(input.initialValue ?? null)

  const { name: inputName } = input
  const { submitting, direction, onChange, onSubmit: formOnSubmit } = React.useContext(FormContext)
  const handleChange = React.useCallback(
    (newValue: string) => {
      // Allow unchecking optional radio
      const shouldUncheckValue = input.required === false && newValue === checkedValue
      if (shouldUncheckValue) {
        setCheckedValue(null)
        onChange(inputName, null)
      } else {
        setCheckedValue(newValue)
        onChange(inputName, newValue)
      }
    },
    [checkedValue, input.required, inputName, onChange]
  )

  const disabled = (submitting || input.disabled) ?? false

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: input.name,
    onChange: handleChange,
    defaultValue: input.initialValue ?? undefined,
  })

  return (
    <Box {...getRootProps()}>
      <Wrap direction="column" spacing="4px" {...props}>
        {input.options.map(item => (
          <InputRadioButton
            key={item.value}
            label={item.label}
            direction={input.direction ?? direction}
            radioPayload={{
              ...getRadioProps({
                value: item.value,
                disabled,
              }),
              isChecked: item.value === checkedValue,
            }}
            buttonProps={buttonProps}
            iconColor={iconColor}
            icon={valueToIcon?.(item.value)}
            formOnSubmit={triggerSubmitOnOptionSelected ? formOnSubmit : undefined}
          />
        ))}
      </Wrap>
    </Box>
  )
}

export default InputRadioButtons
